import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  Constants,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PiWarning } from "react-icons/pi";

import { ExpenseManagementHeader, ExpenseManagementHeaderHandler } from "./Header";
import { Expense } from "Models/expenses";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import { Member } from "Models/members/member";
import { Fleet } from "Models/fleet";
import { ExpensesKeys } from "Util/queries";
import ExpensesService from "Services/expenses.service";
import ConfirmationModal from "Components/Dialogs/ConfirmationModal";
import { Color } from "Components/Form/Button";
import {
  ConfirmationModalBody,
  ConfirmationModalBodyText,
} from "../../../../modules/moving/components/Diary/popups/CancelTask/styles";
import { format } from "date-fns-tz";
import { formatTimeByString } from "Components/TimeControlModal/util";
import { addDays, differenceInMinutes } from "date-fns";
import { ExpenseManagementWrapper, GridWrapper, IconClose, IconEdit } from "./style";
import { useAppState } from "Context/StateContext";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { AxiosError } from "axios";
import { Error } from "Models/error";
import { getEquipmentName } from "Util/getEquipmentName";
import { EquipmentOwnership } from "Util/enums";
import { useExpenseHooks } from "../hooks/useExpenseHooks";

// Interface para expor a funcionalidade de refresh via ref
export interface ExpenseManagementRefHandle {
  refresh: () => void;
}

const HOUR_IN_MINUTES = 60; // 1h = 60min

interface ExpenseManagementProps {
  taskId: number;
  fleets: Fleet[];
  members: Member[];
  taskStart?: string | Date;
  taskEnd?: string | Date;
  isUserEnableToEdit?: boolean;
}

export const ExpenseManagement = React.forwardRef<
  ExpenseManagementRefHandle,
  ExpenseManagementProps
>(
  (
    {
      taskId,
      taskStart,
      taskEnd,
      fleets = [],
      members = [],
      isUserEnableToEdit = true,
    }: ExpenseManagementProps,
    ref
  ) => {
    const { t: translate } = useTranslation();
    const { currentUser } = useAppState();
    const expanseManagementHeaderRef = useRef<ExpenseManagementHeaderHandler>(null);

    const [expense, setExpense] = useState<Expense | null>(null);
    const [isDeleteExpense, setIsDeleteExpense] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [requirement, setRequirement] = useState<string>("");

    const { fetchExpensesList } = useExpenseHooks();

    const {
      refetch: refetchExpenses,
      data: expenses = [],
      isFetching,
    } = fetchExpensesList({
      taskId,
      page,
      pageSize,
      requirement,
      enabled: !!taskId,
    });

    const currency = currentUser?.company?.currency;

    // Helper function to get filter params
    const getFilterParams = () => {
      const isOthersFilter = requirement?.trim() && requirement === "others";
      return {
        memberId: isOthersFilter ? "" : undefined,
        fleetId: isOthersFilter ? "" : undefined,
      };
    };

    const {
      refetch: refetchCount,
      data: expenseTotal,
      isFetching: isFetchingTotal,
    } = useQuery({
      queryKey: ExpensesKeys.count([requirement]),
      queryFn: async () =>
        ExpensesService.count({
          taskId,
          ...getFilterParams(),
        }).then(({ data }) => data),
      enabled: !!taskId,
      initialData() {
        return 0;
      },
      refetchOnWindowFocus: false,
    });

    const {
      refetch: refetchExpensesForTotal,
      data: expensesForTotal = [],
      isFetching: isFetchingForTotal,
    } = fetchExpensesList({
      taskId,
      page: 0,
      pageSize: expenseTotal || 0,
      requirement,
      enabled: !!taskId && !!expenseTotal,
    });

    useImperativeHandle(ref, () => ({
      refresh: () => {
        refetchExpenses();
        refetchCount();
        refetchExpensesForTotal();
      },
    }));

    // Helper function to format currency
    const formatCurrency = (value: number) => {
      return `${currency?.symbol}${value.toFixed(Number(currency?.decimalPoints) || 2)}`;
    };

    // Helper function to calculate cost based on hours
    const calculateCost = (from: string, to: string, rate: number) => {
      let dateTo = to;

      // adds a day if to time is lower than from
      if (new Date(from).getTime() > new Date(to).getTime()) {
        dateTo = addDays(new Date(to), 1).toISOString();
      }

      const hoursWorked = differenceInMinutes(new Date(dateTo), new Date(from)) / HOUR_IN_MINUTES;
      return hoursWorked * rate;
    };

    const columns: Column[] = [
      {
        name: "date",
        title: translate("date"),
        formatComponent: (date) => format(new Date(date), "dd/MM/yyyy"),
        hideSearch: true,
      },
      {
        name: "description",
        title: translate("description"),
        hideSearch: true,
        formatComponent: (description: string, row: Expense) => {
          if (row.member) return row.member.fullName;
          if (row.fleet) {
            const { fullText } = getEquipmentName(
              row.fleet,
              translate("general"),
              translate("fleet")
            );
            return fullText;
          }
          return description;
        },
      },
      {
        name: "type",
        title: translate("type"),
        hideSearch: true,
        formatComponent: (_, row: Expense) => {
          if (row.memberId) return <span>{translate("member")}</span>;
          if (row.fleetId) return <span>{translate("equipment")}</span>;
          return <span>{translate("others")}</span>;
        },
      },
      {
        name: "ratePerHour",
        title: translate("rate"),
        formatComponent: (value) => formatCurrency(value),
        hideSearch: true,
      },
      {
        name: "from",
        title: translate("from"),
        hideSearch: true,
        formatComponent: (value) => formatTimeByString(value) || "-",
      },
      {
        name: "to",
        title: translate("to"),
        hideSearch: true,
        formatComponent: (value) => formatTimeByString(value) || "-",
      },
      {
        name: "ratePerHour",
        title: translate("cost"),
        hideSearch: true,
        formatComponent: (_, row: Expense) => {
          if (row.from && row.to) {
            const cost = calculateCost(row.from, row.to, row.ratePerHour);
            return formatCurrency(cost);
          }
          if (row.ratePerHour) return formatCurrency(row.ratePerHour);
          return "-";
        },
      },
    ];

    const handleEditExpense = (row: Expense) => {
      let option = "others";
      if (row?.memberId) option = "members";
      else if (row?.fleetId) option = "equipments";

      setExpense(row);
      expanseManagementHeaderRef?.current?.setAddNewOption(option);
    };

    const handleDeleteExpense = (row: Expense) => {
      setIsDeleteExpense(true);
      setExpense(row);
    };

    const rowActionsExtra: RowActionsExtra[] = [
      {
        title: translate("edit"),
        key: "edit",
        buttonAndAction: (_, row: Expense) => (
          <Tooltip title={translate("edit") || ""} placement="top">
            <div>
              <IconEdit
                size={Constants.defaultGeneralActionsIconSize}
                color="#475467"
                data-cy="editButton"
                onClick={() => handleEditExpense(row)}
              />
            </div>
          </Tooltip>
        ),
      },
      {
        title: translate("delete"),
        key: "delete",
        buttonAndAction: (_, row: Expense) => (
          <Tooltip title={translate("delete") || ""} placement="top">
            <IconClose onClick={() => handleDeleteExpense(row)}>
              <IconCloseCircle
                color="#475467"
                data-cy="viewButton"
                height={Constants.defaultGeneralActionsIconSize}
                width={Constants.defaultGeneralActionsIconSize}
              />
            </IconClose>
          </Tooltip>
        ),
      },
    ];

    // Helper function to handle mutation success
    const handleMutationSuccess = () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      refetchExpenses();
      refetchCount();
      refetchExpensesForTotal();
      setExpense(null);
      expanseManagementHeaderRef?.current?.clearAfterSubmit();
    };

    // Helper function to handle mutation error
    const handleMutationError = (error: AxiosError<Error>) => {
      if (error?.response) NotificationToast.error(error?.response?.data?.message);
    };

    const { mutate: onCreate, isLoading } = useMutation({
      mutationKey: ExpensesKeys.create(),
      mutationFn: async (expense: Expense) => await ExpensesService.create(expense),
      onSuccess: handleMutationSuccess,
      onError: handleMutationError,
    });

    const { mutate: onEdit, isLoading: isLoadingUpdate } = useMutation({
      mutationKey: ExpensesKeys.create(),
      mutationFn: async (expense: Expense) => await ExpensesService.update(expense.id!, expense),
      onSuccess: handleMutationSuccess,
      onError: handleMutationError,
    });

    const { mutate: onDelete, isLoading: isDeleting } = useMutation({
      mutationKey: ExpensesKeys.delete(),
      mutationFn: async (id: number) => await ExpensesService.delete(id),
      onSuccess: () => {
        handleMutationSuccess();
        setIsDeleteExpense(false);
      },
      onError: handleMutationError,
    });

    const handleCurrentPage = (numPage: number) => {
      setPage(numPage - 1);
    };

    const onSubmit = () => {
      if (!expense) return;

      let totalCost = expense.ratePerHour || 0;

      if (expense.from && expense.to) {
        totalCost = Number(
          calculateCost(expense.from, expense.to, expense.ratePerHour).toFixed(
            Number(currency?.decimalPoints) || 2
          )
        );
      }

      if (!expense?.id) {
        onCreate({ ...expense, taskId, totalCost });
        return;
      }

      onEdit({ ...expense, totalCost });
    };

    // Simplified calculation of total expenses
    const totalExpense = useMemo(() => {
      const sourceExpenses = expensesForTotal;

      if (!sourceExpenses?.length) return {};

      const calculateExpenseCost = (expense: Expense): number => {
        if (!expense?.to || !expense.from) return expense.ratePerHour || 0;
        return calculateCost(expense.from, expense.to, expense.ratePerHour);
      };

      // Group expenses by type
      const ownMemberExpenses = sourceExpenses.filter(
        (e) => e.memberId && !e.member?.subcontractor
      );
      const ownFleetExpenses = sourceExpenses.filter(
        (e) =>
          e.fleetId &&
          e.fleet?.ownership?.toLowerCase() !== EquipmentOwnership.SUBCONTRACTOR.toLowerCase()
      );
      const subMemberExpenses = sourceExpenses.filter((e) => e.memberId && e.member?.subcontractor);
      const subFleetExpenses = sourceExpenses.filter(
        (e) =>
          e.fleetId &&
          e.fleet?.ownership?.toLowerCase() === EquipmentOwnership.SUBCONTRACTOR.toLowerCase()
      );
      const otherExpenses = sourceExpenses.filter((e) => !e.memberId && !e.fleetId);

      // Calculate totals
      const sumExpenses = (items: Expense[]) =>
        items.reduce((sum, exp) => sum + calculateExpenseCost(exp), 0);

      const totalPerOwn =
        sumExpenses(ownMemberExpenses) + sumExpenses(ownFleetExpenses) + sumExpenses(otherExpenses);

      const totalPerSubcontractor = sumExpenses(subMemberExpenses) + sumExpenses(subFleetExpenses);

      return {
        total: totalPerOwn + totalPerSubcontractor,
        totalPerOwn,
        totalPerSubcontractor,
      };
    }, [expensesForTotal]);

    useEffect(() => {
      if (taskStart && !expense?.date) {
        setExpense(
          (prev) =>
            ({
              ...(prev || {}),
              date: new Date(taskStart).toISOString(),
            } as unknown as Expense)
        );
      }
    }, [taskStart, expense]);

    return (
      <ExpenseManagementWrapper container item direction="row">
        <ExpenseManagementHeader
          ref={expanseManagementHeaderRef}
          expense={expense}
          totalExpense={totalExpense}
          isLoading={isLoading || isLoadingUpdate}
          fleets={fleets}
          requirement={requirement}
          setRequirement={setRequirement}
          members={members}
          onSubmit={onSubmit}
          setExpense={setExpense}
          taskStart={taskStart}
          taskEnd={taskEnd}
          isUserEnableToEdit={isUserEnableToEdit}
        />
        <GridWrapper>
          <ShipeeziNewGrid
            data={expenses}
            columns={columns}
            loadingData={isFetching || isFetchingTotal || isFetchingForTotal}
            totalItems={expenseTotal}
            emptyData={translate("noInformationToBeShown")}
            rowActionsExtra={isUserEnableToEdit ? rowActionsExtra : []}
            positionActionsColumn="right"
            showSelection={false}
            pageSize={pageSize}
            currentPage={page}
            alwaysShowFooter
            pageSizeList={[10, 20, 40, 100]}
            onChangePageSize={setPageSize}
            onNextPage={handleCurrentPage}
            onPreviousPage={handleCurrentPage}
            onGoToPage={handleCurrentPage}
          />

          <ConfirmationModal
            open={isDeleteExpense}
            closeModal={() => setIsDeleteExpense(false)}
            title={`${translate("areYouSure")}?`}
            leftIconTitle={<PiWarning color={colors.error600} />}
            submit={() => expense?.id && onDelete(expense.id)}
            textSubmit={translate("delete")}
            textCancel={translate("cancel")}
            customColorCancel={Color.TEXT_INFO}
            customColorSave={Color.ERROR}
            loading={isDeleting}
            type="alert"
            maxWidth="520px"
            showCancelButton
          >
            <ConfirmationModalBody>
              <ConfirmationModalBodyText>
                {translate("thisActionCannotBeUndone")}.{" "}
                {translate("onceDeletedThisEntryWillBePermanentlyRemoved")}
              </ConfirmationModalBodyText>
            </ConfirmationModalBody>
          </ConfirmationModal>
        </GridWrapper>
      </ExpenseManagementWrapper>
    );
  }
);
